// ==== Media Breakpoints ====
// Viewports represent the lower limit of the breakpoint range, ie, minS: 320-479px
// See https://www.figma.com/file/X2w9NdiyCKVvZwLYv8nnQ2/AMC-Fabric-POR?node-id=263%3A0

import { createGlobalState, useMount, useUnmount } from "@amx/ux-twoui-utils";
import { PerViewport, ViewportType, viewportTypes } from "../shared/constants";

/**
 * Combination of Image sizes and Backplate sizes.
 * Key: Image size.
 * Value: Backplate size based off mappings listed in Figma.
 */
export const mapImageBackplateSize = {
    "12px": "16px",
    "14px": "20px",
    "16px": "24px",
    "24px": "32px",
    "32px": "40px",
    "48px": "64px",
    "60px": "72px",
    "72px": "88px",
    "96px": "112px",
    "246px": "246px",
    "186px": "186px",
    "220px": "220px",
    "270px": "270px",
};

/** Type of `ImageSize` variation */
export type ImageSize = keyof typeof mapImageBackplateSize;

// note, we very specifically _don't_ use the "Size" type out of constants, specifically
// because it is overloaded and used for all sorts of different purposes in different components.
// Here, we follow the naming convention of actual Viewport Types defined in the Figma designs.
type ViewPortBounds = PerViewport<number>;

export const viewportWidthMin: ViewPortBounds = {
    s: 0,
    m: 480,
    l: 640,
    xl: 1024,
    xxl: 1366,
    xxxl: 1920,
};

const viewportWidthMax: ViewPortBounds = {
    s: viewportWidthMin.m - 1,
    m: viewportWidthMin.l - 1,
    l: viewportWidthMin.xl - 1,
    xl: viewportWidthMin.xxl - 1,
    xxl: viewportWidthMin.xxxl - 1,
    xxxl: Number.MAX_SAFE_INTEGER,
};

const viewportTypeFromPixelWidth = (pixelWidth: number): ViewportType => {
    return viewportTypes.find(v => pixelWidth <= viewportWidthMax[v]) || "xxxl";
};

export const media = {
    minS: `@media only screen and (min-width: ${viewportWidthMin.s}px)`,
    minM: `@media only screen and (min-width: ${viewportWidthMin.m}px)`,
    minL: `@media only screen and (min-width: ${viewportWidthMin.l}px)`,
    minXl: `@media only screen and (min-width: ${viewportWidthMin.xl}px)`,
    minXxl: `@media only screen and (min-width: ${viewportWidthMin.xxl}px)`,
    minXxxl: `@media only screen and (min-width: ${viewportWidthMin.xxxl}px)`,
    maxS: `@media only screen and (max-width: ${viewportWidthMax.s}px)`,
    maxM: `@media only screen and (max-width: ${viewportWidthMax.m}px)`,
    maxL: `@media only screen and (max-width: ${viewportWidthMax.l}px)`,
    maxXl: `@media only screen and (max-width: ${viewportWidthMax.xl}px)`,
    maxXxl: `@media only screen and (max-width: ${viewportWidthMax.xxl}px)`
};

export const heightMedia = {
    minS: `@media only screen and (min-height: 0px)`,
    minM: `@media only screen and (min-height: 420px)`
};

export const highContrastMedia = {
    active: "@media only screen and (-ms-high-contrast: active), (forced-colors: active)",
    blackOnWhite: "@media only screen and (-ms-high-contrast: black-on-white), (forced-colors: active) and (prefers-color-scheme: light)",
    whiteOnBlack: "@media only screen and (-ms-high-contrast: white-on-black), (forced-colors: active) and (prefers-color-scheme: dark)"
};

const viewportType = createGlobalState<ViewportType>(viewportTypeFromPixelWidth(window.innerWidth));

const viewPortProp = "x-viewport";

// Create media queries for each viewport. If window.matchMedia doesn't exist, such as in jsdom, don't initialize media queries
const mediaMatchers = window.matchMedia ? viewportTypes.map(v => {
    const matcher = window.matchMedia(`(max-width: ${viewportWidthMax[v]}px) and (min-width: ${viewportWidthMin[v]}px)`);
    matcher[viewPortProp] = v;
    return matcher;
}) : undefined;

const updateViewportWidth = (ev: MediaQueryListEvent): void => {
    if (ev.matches) {
        // In IE, the viewPortProp is on the event and there is no ev.currentTarget
        const newViewportType = ev[viewPortProp] || ev.currentTarget?.[viewPortProp] || viewportTypeFromPixelWidth(window.innerWidth);
        viewportType.setValue(newViewportType);
    }
};

export const useWatchViewport = (): void => {
    useMount(() => {
        if (mediaMatchers) {
            mediaMatchers.forEach((m) => {
                m.addListener(updateViewportWidth);
            });
        }

        viewportType.setValue(viewportTypeFromPixelWidth(window.innerWidth));
    });

    useUnmount(() => {
        if (mediaMatchers) {
            // don't forget to clean up
            mediaMatchers.forEach(m => m.removeListener(updateViewportWidth));
        }
    });
};

export const useViewport = (): ViewportType => {
    return viewportType.useValue();
};

export const isMobileViewport = (viewport: ViewportType): boolean => {
    // Design agreed with small and medium sizes are consider as mobile viewport.
    return (["s", "m"].includes(viewport));
};
